import {
  Component,
  EventEmitter,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Project } from '../../../../model/project/project';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { RequestService } from '../../../../services/request.service';
import {
  catchError,
  debounceTime,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { merge, of as observableOf } from 'rxjs';
import { FilterEnabledComponent } from '../../../../helpers/filter-enabled.component';
import ProductHelper from '../../../../helpers/product-helper';
import { FilterStoreService } from 'src/app/services/filter-store.service';
import {
  PAGE_SIZE_OPTIONS,
  PageIndexService,
} from 'src/app/services/page-index-service';
import Address from 'src/app/model/project/address';
import { StringHelper } from 'src/app/helpers/string-helper';
import { SubProject } from '../../../../model/project/sub-project';
import ProjectHelper from 'src/app/helpers/project-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import Board from 'src/app/model/product/board/board';

@Component({
  selector: 'search-projects',
  templateUrl: './search-projects.component.html',
  styleUrls: ['./search-projects.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchProjectsComponent
  extends FilterEnabledComponent
  implements OnInit
{
  projects: SubProject[];
  totalItems = 0;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  reloadData: EventEmitter<any>;
  includeRepo = true;
  loading = false;
  data: Project[] = [];
  selectedTypeOptions = [];
  displayedColumns: string[] = [
    'project_id',
    'company',
    'projectPlacement.address',
    'productType',
    'boardTypeAndPlacement',
    'displayStatus',
  ];

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(
    private requestService: RequestService,
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    private cd: ChangeDetectorRef
  ) {
    super(
      [
        { key: 'id', active: false, strictEquality: true, numeric: true },
        {
          key: 'project__id',
          active: false,
          strictEquality: true,
          numeric: true,
        },
        {
          key: 'projectPlacement__address',
          active: false,
          strictEquality: true,
          numeric: false,
        },
        {
          key: 'project__projectContacts__company__regularName',
          active: false,
          strictEquality: false,
          numeric: false,
        },
      ],
      filterStoreService,
      pageIndexService
    );
    this.projects = [];
    let storedTypeOptions = this.filterStoreService.getTypeOptions(
      this.getLocalStorageKey()
    );
    if (storedTypeOptions != null && storedTypeOptions.length > 0) {
      this.selectedTypeOptions = storedTypeOptions;
    }
  }

  ngOnInit() {
    // If the user changes the sort order, reset back to the first page.
    this.reloadData = new EventEmitter<any>();

    this.paginator.pageIndex = this.initPageIndex;
    this.paginator.pageSize = this.initPageSize;

    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
    });

    this.filtersChanged.pipe(debounceTime(800)).subscribe(() => {
      this.reloadData.emit();
    });

    merge(this.sort.sortChange, this.paginator.page, this.reloadData)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.loading = true;
          this.pageIndexService.setPageIndex(
            this.getLocalStorageKey(),
            this.paginator.pageIndex
          );
          this.pageIndexService.setPageSize(
            this.getLocalStorageKey(),
            this.paginator.pageSize
          );
          this.filterStoreService.setTypeOptions(
            this.getLocalStorageKey(),
            this.selectedTypeOptions
          );
          this.cd.markForCheck();
          return this.requestService.postFetch(
            this.generateTypeCriteria(),
            '/subprojects/search/' + this.includeRepo, //TODO set correct endpoints
            this.sort.active,
            this.sort.direction,
            this.paginator.pageSize,
            this.paginator.pageIndex,
            this.generateFilterString()
          );
        }),
        map((data) => {
          this.totalItems = Number.parseInt(data.headers.get('X-Total-Count'));
          return data.body;
        }),
        catchError(() => {
          return observableOf([]);
        })
      )
      .subscribe((data) => {
        this.projects = data;
        this.projects.forEach((p) => {
          if (p.project != null) {
            p.project.involvedCompanies = ProjectHelper.getDtoInvolvedCompanies(
              p.project
            );
            if (CollectionHelper.isPopulated(p.project.products)) {
              p.project.products.forEach((prod) => {
                if (ProductHelper.isBoard(prod)) {
                  ProductHelper.calculateTableBoardDisplay(<Board>prod);
                }
              });
            }
          }
        });
        this.loading = false;
        if (
          (data == null || data.length === 0) &&
          this.paginator.pageIndex > 0
        ) {
          this.paginator.pageIndex = 0;
          this.reloadData.next();
        }
        this.cd.markForCheck();
      });
  }

  translateType(type: string, otherProductName: string) {
    return ProductHelper.getAbbreviatedType(type, otherProductName);
  }

  protected getLocalStorageKey(): string {
    return 'SearchProjectsComponent';
  }

  generateTypeCriteria() {
    return this.selectedTypeOptions != null &&
      this.selectedTypeOptions.length > 0
      ? {
          typeCriteria: {
            project__products: this.selectedTypeOptions,
          },
        }
      : { typeCriteria: {} };
  }

  printAddress(address: Address) {
    let cityPart = StringHelper.join([address.postalCode, address.city], ' ');
    return StringHelper.join([address.line1, cityPart], ', ');
  }

  getStatusString(row) {
    return ProjectHelper.getTypeStatusString(row);
  }

  getType(row) {
    return ProjectHelper.getType(row);
  }

  getTypeTooltip(type: string, otherProductName) {
    return ProductHelper.getType(type, otherProductName);
  }

  public getRouterLinkCreateOldSystemProject() {
    return '/create-old-system-project';
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}
